import type { TemplateFormField } from '@common/types/templateFormFields';
import { produce } from 'immer';
import { create } from 'zustand';
import { getClientSideQuoteForms } from '@/services/ClientSideTemplateFormService';

export type UseQuoteFormClientViewStoreType = {
    quoteProcess: string;
    formData: {
        quoteForm: {
            isSubmitted: boolean;
            templateFormId: string | null;
            quoteTemplateId: string | null;
            templateFormType: string;
            headerDescription: string;
            footerTemplate: string;
            templateFormFields: TemplateFormField[];
        };
    };
    loading: boolean;
    error: string | null;
    updateVisibleToUser: (fieldName: string, isVisibleToUser: boolean) => void;
    updateTemplateFormField: (value: TemplateFormField, fieldName: string) => void;
    setIsSubmitted: () => void;
    setQuoteFormData: (value: string, fieldName: string) => void;
    setQuoteFormErrorData: (value: any) => void;
    fetchQuoteFormData: (quoteTemplateId: string) => Promise<void>;
    isQuoteFormPresent: (quoteTemplateId: string) => Promise<void>;
    setError: (error: string | null) => void;
    setLoading: (isLoading: boolean) => void;
};

const useQuoteFormClientViewStore = create<UseQuoteFormClientViewStoreType>((set) => ({
    quoteProcess: '',
    formData: {
        quoteForm: {
            isSubmitted: false,
            headerDescription: '',
            footerTemplate: '',
            quoteTemplateId: null,
            templateFormFields: [],
            templateFormId: null,
            templateFormType: 'QuoteForm',
        },
    },
    loading: false,
    error: null,
    updateVisibleToUser: (fieldName: string, isVisibleToUser: boolean) => {
        set((state) => {
            const templateFormFieldsData = produce(state, (draftState) => {
                const field = draftState.formData.quoteForm.templateFormFields.find(
                    (f) => f.questionSchema.name === fieldName,
                );
                if (field) {
                    field.isVisibleToUser = isVisibleToUser; // or some other value
                }
            });

            return {
                formData: {
                    ...state.formData,
                    quoteForm: {
                        ...state.formData.quoteForm,
                        templateFormFields: [
                            ...templateFormFieldsData.formData.quoteForm.templateFormFields,
                        ],
                    },
                },
            };
        });
    },
    updateTemplateFormField: (value: any, fieldName: string) => {
        set((state) => {
            const newFields = state.formData.quoteForm.templateFormFields.map((field) => {
                if (field.questionSchema.name === fieldName) {
                    return {
                        ...field,
                        questionSchema: {
                            ...field.questionSchema,
                            ...value.questionSchema,
                        },
                    };
                } else {
                    return field;
                }
            });
            return {
                formData: {
                    ...state.formData,
                    quoteForm: {
                        ...state.formData.quoteForm,
                        templateFormFields: newFields,
                    },
                },
            };
        });
    },
    setQuoteFormErrorData: (value: any) =>
        set((state) => {
            return {
                formData: {
                    ...state.formData,
                    quoteForm: {
                        ...state.formData.quoteForm,
                        templateFormFields: value,
                    },
                },
            };
        }),
    setQuoteFormData: (value: string, fieldName: string) =>
        set((state) => {
            const newFields = state.formData.quoteForm.templateFormFields.map((field) => {
                if (field.questionSchema.name === fieldName) {
                    return {
                        ...field,
                        questionSchema: {
                            ...field.questionSchema,
                            value: value,
                        },
                    };
                } else {
                    return field;
                }
            });
            return {
                formData: {
                    ...state.formData,
                    quoteForm: {
                        ...state.formData.quoteForm,
                        templateFormFields: newFields,
                    },
                },
            };
        }),
    setIsSubmitted: () =>
        set((state) => ({
            formData: {
                ...state.formData,
                quoteForm: {
                    ...state.formData.quoteForm,
                    isSubmitted: true,
                },
            },
        })),
    fetchQuoteFormData: async (quoteTemplateFormId: string) => {
        set((_state) => ({
            loading: true,
        }));
        try {
            const quoteFormTemplateResp = await getClientSideQuoteForms(quoteTemplateFormId);
            const {
                templateFormFields,
                footerTemplate,
                headerDescription,
                quoteTemplateId,
                templateFormId,
            } = quoteFormTemplateResp.data;
            set((state) => ({
                formData: {
                    ...state.formData,
                    quoteForm: {
                        ...state.formData.quoteForm,
                        headerDescription: headerDescription ?? '',
                        footerTemplate: footerTemplate ?? '',
                        quoteTemplateId: `${quoteTemplateId}` || null,
                        templateFormId: templateFormId || null,
                        templateFormFields: templateFormFields,
                    },
                },
            }));
            set((_state) => ({
                quoteProcess: quoteFormTemplateResp.data.process,
            }));
            set((_state) => ({
                loading: false,
            }));
        } catch (error: any) {
            // set error for snackbar
            set((_state) => ({
                error: error?.response?.data || 'Something went wrong!',
            }));
            set((_state) => ({
                loading: false,
            }));
            throw error;
        } finally {
            set((_state) => ({
                loading: false,
            }));
        }
    },
    isQuoteFormPresent: async (quoteTemplateFormId: string) => {
        set((_state) => ({
            loading: true,
        }));
        try {
            await getClientSideQuoteForms(quoteTemplateFormId);
            set((_state) => ({
                loading: false,
            }));
        } catch (error: any) {
            // set error for snackbar
            set((_state) => ({
                error: error?.response?.data || 'Something went wrong!',
            }));
            throw error;
        } finally {
            set((_state) => ({
                loading: false,
            }));
        }
    },
    setError: (error: string | null) => {
        set((_state) => ({
            error: error,
        }));
    },
    setLoading: (isLoading: boolean) => {
        set((_state) => ({
            loading: isLoading,
        }));
    },
}));

export default useQuoteFormClientViewStore;
